import { createFetch } from '@vueuse/core'
import { defineStore } from 'pinia'

import router from '@/router'
import i18n from '@/libs/i18n'
import { Errors } from '@/libs/error'
import { PrintError } from '@/libs/toast'
import { useAppStore } from '@/stores/app'

/**
 * Whether a failed call is the retail counter saying it is locked, rather than the
 * portal saying the login has expired.
 *
 * The body is read defensively: it arrives already parsed for a `.json()` call, and
 * a 401 from anywhere else in the stack (a proxy, a gateway) carries no body of ours
 * at all. Anything that is not recognisably the counter's own code is treated as a
 * portal session expiry, which is the safe way round — the worst case is the sign-in
 * screen a person can act on, not a terminal quietly using a session it no longer has.
 */
const isTillLocked = (body: unknown): boolean =>
    (body as { error?: { code?: number } } | null)?.error?.code === Errors.TillLocked

export const useFetchStore = defineStore('fetch', () => {
    const appStore = useAppStore()

    /**
     * NO CLIENT TIMEOUT, AND THAT IS DELIBERATE.
     *
     * `createFetch` takes one and this does not pass it, so nothing here abandons a
     * call the server is still answering. The service decides how long a request may
     * take — 60 s for an ordinary read, 5 min for one turn of the assistant
     * (`HTTP_API/Routes.go`, docs/AI.md §19) — and a browser that gave up first would
     * throw away an answer that was about to arrive, having already paid for it: a
     * model call is billed whether or not anybody reads the reply.
     *
     * What a person gets instead of a shorter wall is being told the wait is normal:
     * the assistant screen shows „Milo dela …" after five seconds
     * (`assistant.still_working`). If a call ever does need to be given up on, it is
     * given up on by the screen that made it, with an AbortSignal it owns.
     */
    const fetch = createFetch({
        baseUrl: '/api/pms/v1',
        options: {
            /**
             * KEEP THE BODY OF A FAILED CALL.
             *
             * VueUse throws it away by default, and that one default silently disabled
             * the whole error-message system of this portal. Every screen calls
             * `parseError(data.value, fallback)` — and on any refusal `data.value` was
             * null, so `parseError` never saw the code, never matched a case, and
             * every screen printed its generic fallback.
             *
             * What that looked like at a desk: a check-out refused because the account
             * issuing the invoice has no tax number (409, code 10) — an error the
             * server explains precisely, in the user's own language, naming the
             * setting to fix — arrived as "the invoice could not be issued, fix the
             * cause and try again", with the cause nowhere on the screen.
             *
             * The refusals still ARE errors: `error` is set and every `statusCode`
             * check in the product is untouched. This only stops the reason from being
             * discarded on the way.
             */
            updateDataOnError: true,

            beforeFetch({ options }) {
                options.headers = new Headers(options.headers)
                options.headers.set('Authorization', `Bearer ${appStore.getJWT()}`)

                return { options }
            },
            async onFetchError(ctx) {
                // The retail counter's own refusal, which shares the 401 and means
                // something else entirely: the PIN was wrong, or the counter was
                // never given to that person, or the counter session ran out. The
                // PORTAL session is untouched — the terminal is still signed in, and
                // throwing it back to the front door loses a login nobody lost, in
                // the middle of a shift, in front of a customer.
                //
                // This is not theoretical. The lock screen offers every counter in
                // the house and starts on the first one, so a seller assigned to a
                // different counter is refused on their very first attempt: right
                // PIN, wrong drawer, and the whole terminal signed itself out. The
                // screen shows its own red message and keeps the number pad instead.
                if (isTillLocked(ctx.data)) return ctx

                // A refused sign-in is not an expired session: the login screen says
                // "wrong username or password" itself, and a second message beside it
                // about a session that ran out sends somebody who simply mistyped a
                // password looking for a fault that is not there.
                if (ctx.response?.status === 401 && appStore.isLoggedIn()) {
                    // Say why. A session that has run out looks, from inside a dialog,
                    // exactly like the thing the dialog was trying to do having failed —
                    // "the room could not be checked in" — and somebody then goes
                    // looking for a fault in the guest's passport. The screen that
                    // caused it may still print its own message; this one is the true
                    // one, and the login screen behind it makes it obvious.
                    PrintError(i18n.global.t('errors.session_expired'))

                    appStore.setLogout()
                    await router.push({ name: 'login' })
                }

                return ctx
            },
        },
    })

    return { fetch }
})
